import { warnBeforeUnload } from '../utils/warn-before-unload';
import {
    CanDeactivateComponent,
    CanDeactivateType,
} from './can-deactivate-guard';

interface UnsavedChangesGuardOptions {
    /** Single source of truth read by both the router guard and the beforeunload listener. */
    hasUnsavedChanges: () => boolean;
    /** Called only when `hasUnsavedChanges()` is true; owns the in-app confirm dialog/message. */
    confirm: () => CanDeactivateType;
}

/**
 * Wires a component's `CanDeactivate` handling and browser-refresh/close
 * protection ({@link warnBeforeUnload}) off one shared dirty check. In-app
 * navigation still gets the page's own custom confirm dialog via `confirm`;
 * browser unload always shows the generic native prompt — the two mechanisms
 * can share their trigger condition but not their UI, see `warnBeforeUnload`
 * for why. Must be called from an injection context (constructor or field
 * initializer).
 */
export function createUnsavedChangesGuard(
    options: UnsavedChangesGuardOptions
): CanDeactivateComponent {
    warnBeforeUnload(options.hasUnsavedChanges);

    return {
        canDeactivate: (): CanDeactivateType =>
            options.hasUnsavedChanges() ? options.confirm() : true,
    };
}
